/**
 * Boundary Node Component for React Flow
 *
 * Renders the Smart Asset boundary as a dashed rounded rectangle.
 * This node sits behind all internal nodes and visually separates
 * what's inside vs outside the Smart Asset.
 *
 * @see https://gitlab.com/smart-assets.io/smart-asset-primitives/gitlab-profile/-/blob/master/docs/SmartAssetSpec/MockSmartAssetTemplates/SampleSmartTreasury.md
 */
import { memo } from "react";
import type { NodeProps } from "reactflow";

export interface BoundaryNodeData {
  /** Boundary label (e.g., "Smart Treasury Boundary") */
  label: string;
  /** Width of the boundary rectangle */
  width: number;
  /** Height of the boundary rectangle */
  height: number;
}

export const BoundaryNode = memo(({ data }: NodeProps<BoundaryNodeData>) => {
  return (
    <div
      className="relative"
      style={{
        width: data.width,
        height: data.height,
        // Ensure boundary is behind other nodes and doesn't capture mouse events
        zIndex: -1,
        pointerEvents: "none",
      }}
    >
      {/* Dashed border rectangle */}
      <div
        className="absolute inset-0 rounded-2xl border-2 border-dotted border-sa-border"
        style={{ pointerEvents: "none" }}
      />

      {/* Boundary label at top-left */}
      <div className="absolute -top-3 left-4 px-2 py-0.5 text-sm font-medium text-sa-text bg-sa-surface-hover rounded">
        {data.label}
      </div>
    </div>
  );
});

BoundaryNode.displayName = "BoundaryNode";
